import React from 'react';
import PropTypes from 'prop-types';
import { get, map, pick } from 'lodash/fp';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { GuestType } from '../../../interfaces/guest';
import guestAddressLine from '../../../helpers/guestAddressLine';
import RedLabel from '../../RedLabel';
import { getAgeFields } from '../../../redux/selectors/client';
import { VIEW_ADDRESS } from '../../../constants/permissions/guest';
import { viewAccess } from '../../../helpers/permission';
import formatFullName from '../../../helpers/formatFullName';
import AccessControl from '../../AccessControl';

const DetailsItem = styled.div`
  font-size: 15px;
  margin-bottom: 5px;
  > span:first-child {
    width: 170px;
    color: #999;
    display: inline-block;
    vertical-align: top;
  }
  > span:nth-child(2) {
    width: 180px;
    display: inline-block;
    vertical-align: top;
    line-break: anywhere;
  }
`;

const ContactNumbers = styled.div`
  display: inline-block;
  > div > small {
    color: #999;
  }
`;

export const AgeBreakdown = styled.div`
  font-size: 15px;
  float: right;
  > div > span:first-child {
    width: 90px;
    display: inline-block;
  }
`;

export const AgeBreakdownLabel = styled.div`
  color: #999;
  display: inline-block;
  > span:first-child {
    width: 30px;
    display: inline-block;
  }
`;

const GuestDetails = ({
  title,
  guest,
  showCounty,
  showEmail,
  showHomePhone,
  showWorkPhone,
  showCellPhone,
  showAgeBreakdown,
  ...props
}) => {
  const fullName = formatFullName(pick(['firstname', 'lastname'], guest));
  const email = get('email', guest);
  const householdTotal = get('household_total', guest);
  const isHomeless = get('homeless', guest);
  const homePhone = get('phone', guest);
  const workPhone = get('workphone', guest);
  const cellPhone = get('cellphone', guest);
  const address = guestAddressLine(guest, true, showCounty);
  const ageFields = useSelector(getAgeFields);

  return (
    <div {...props} style={{ padding: 10 }}>
      <h5 style={{ marginBottom: 10 }}>{title}</h5>
      <DetailsItem>
        <span>ID: </span>
        <span>{get('id', guest)}</span>
      </DetailsItem>
      <DetailsItem>
        <span>Name: </span>
        <span>{fullName}</span>
      </DetailsItem>
      {isHomeless ? (
        <DetailsItem>
          <span>Homeless:</span>
          <RedLabel>Yes</RedLabel>
        </DetailsItem>
      ) : (
        <DetailsItem>
          <span>Address: </span>
          <span>
            <AccessControl allowOnlyFor={viewAccess(VIEW_ADDRESS)}>
              {address.line1}
            </AccessControl>
            {address.line2}
          </span>
        </DetailsItem>
      )}
      <DetailsItem>
        <span>Contact Number(s): </span>
        <ContactNumbers>
          {!homePhone && !workPhone && !cellPhone && 'None'}
          {showHomePhone &&
            homePhone && (
              <div>
                <small>Home: </small>
                {homePhone}
              </div>
            )}
          {showWorkPhone &&
            workPhone && (
              <div>
                <small>Work: </small>
                {workPhone}
              </div>
            )}
          {showCellPhone &&
            cellPhone && (
              <div>
                <small>Cell: </small>
                {cellPhone}
              </div>
            )}
        </ContactNumbers>
      </DetailsItem>
      {showEmail &&
        email && (
          <DetailsItem>
            <span>Email: </span>
            <span>{email}</span>
          </DetailsItem>
        )}
      <DetailsItem>
        <span>Total Individual: </span>
        <span>{householdTotal} person(s)</span>
      </DetailsItem>
      {showAgeBreakdown && (
        <DetailsItem>
          <span>Household Age Group: </span>
          <AgeBreakdown>
            <AgeBreakdownLabel>
              <span>Age Group</span>
              <span># of Individual</span>
            </AgeBreakdownLabel>
            {map(
              ({ name: ageField, label }) => (
                <div key={ageField}>
                  <span>{label}</span>
                  <span>{get(ageField, guest)}</span>
                </div>
              ),
              ageFields
            )}
          </AgeBreakdown>
        </DetailsItem>
      )}
    </div>
  );
};

GuestDetails.propTypes = {
  guest: GuestType.isRequired,
  showCounty: PropTypes.bool.isRequired,
  showEmail: PropTypes.bool.isRequired,
  showHomePhone: PropTypes.bool.isRequired,
  showWorkPhone: PropTypes.bool.isRequired,
  showCellPhone: PropTypes.bool.isRequired,
  showAgeBreakdown: PropTypes.bool.isRequired,
  title: PropTypes.string
};

GuestDetails.defaultProps = {
  title: 'Guest Details'
};

export default GuestDetails;
